// Professional Notification System - Replaces scattered toast notifications
import React from 'react';
import ReactDOM from 'react-dom';
import {
  Button,
  Text,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from '../design-system';
import { useApp } from '../contexts/AppContext';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// Individual notification component
const Notification = ({ notification, onClose }) => {
  const { id, type, message, title, autoHide = true } = notification;

  // Get icon and colors based on type
  const getNotificationStyle = (type) => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircleIcon,
          iconColor: T.moss,
          backgroundColor: `${T.moss}14`,
          borderLeftColor: T.moss,
          titleColor: T.moss,
          messageColor: T.ink2,
          progressColor: `${T.moss}55`,
        };
      case 'error':
        return {
          icon: ExclamationCircleIcon,
          iconColor: T.capRed,
          backgroundColor: `${T.capRed}10`,
          borderLeftColor: T.capRed,
          titleColor: T.capRed,
          messageColor: T.ink2,
          progressColor: `${T.capRed}44`,
        };
      case 'warning':
        return {
          icon: ExclamationCircleIcon,
          iconColor: T.orange,
          backgroundColor: `${T.orange}12`,
          borderLeftColor: T.orange,
          titleColor: T.orangeDark,
          messageColor: T.ink2,
          progressColor: `${T.orange}44`,
        };
      case 'info':
      default:
        return {
          icon: InformationCircleIcon,
          iconColor: T.ink2,
          backgroundColor: `${T.ink2}0C`,
          borderLeftColor: T.ink2,
          titleColor: T.ink2,
          messageColor: T.inkMuted,
          progressColor: `${T.ink2}33`,
        };
    }
  };

  const style = getNotificationStyle(type);
  const Icon = style.icon;

  return (
    <div
      style={{
        position: 'relative',
        minWidth: '320px',
        maxWidth: '480px',
        backgroundColor: T.card,
        border: `1px solid ${T.rule}`,
        borderLeft: `3px solid ${style.borderLeftColor}`,
        borderRadius: T.r3,
        padding: '14px 16px',
        marginBottom: '8px',
        boxShadow: T.sh2,
        animation: 'slideIn 0.3s ease-out',
        fontFamily: T.sans,
        overflow: 'hidden',
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
      }}>
        <Icon size={20} style={{ color: style.iconColor, marginTop: '1px', flexShrink: 0 }} />

        <div style={{ flex: 1, minWidth: 0 }}>
          {title && (
            <Text style={{
              fontWeight: 700,
              fontSize: '13px',
              margin: `0 0 3px 0`,
              color: style.titleColor,
              fontFamily: T.sans,
            }}>
              {title}
            </Text>
          )}

          <Text style={{
            fontSize: '13px',
            lineHeight: '1.45',
            margin: 0,
            color: style.messageColor,
            fontFamily: T.sans,
          }}>
            {message}
          </Text>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onClose(id)}
          style={{
            padding: '2px',
            minWidth: 'auto',
            height: 'auto',
            color: T.inkFaint,
            backgroundColor: 'transparent',
            flexShrink: 0,
          }}
        >
          ×
        </Button>
      </div>

      {/* Auto-hide progress bar */}
      {autoHide && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '2px',
          backgroundColor: style.progressColor,
          transformOrigin: 'left',
          animation: 'shrink 5s linear forwards',
        }} />
      )}
    </div>
  );
};

// Notification container
const NotificationContainer = () => {
  const { notifications, removeNotification } = useApp();

  if (notifications.length === 0) {
    return null;
  }

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed',
      top: '16px',
      right: '16px',
      zIndex: 9999,
      pointerEvents: 'none',
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '8px',
      }}>
        {notifications.map(notification => (
          <div
            key={notification.id}
            style={{ pointerEvents: 'auto' }}
          >
            <Notification
              notification={notification}
              onClose={removeNotification}
            />
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

// CSS animations for the notification system
const notificationStyles = `
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes shrink {
    from {
      transform: scaleX(1);
    }
    to {
      transform: scaleX(0);
    }
  }
`;

// Inject styles if not already present
if (!document.querySelector('#notification-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'notification-styles';
  styleSheet.textContent = notificationStyles;
  document.head.appendChild(styleSheet);
}

export default NotificationContainer;
